import {
  ErrorMessage,
  FloatingLabel,
  FloatingLabelContainer,
  FloatingLabelContainerWithAddon,
  FloatingLabelInput,
  FloatingLabelInputNumberFormat,
} from 'src/components/Inputs/FloatingLabel';
import { Box, Flex, Icon, Select, Text } from '@chakra-ui/react';
import { CartItemType } from 'src/types/ui';
import { useEventContext } from '../../provider';
import { FaRegCalendar } from 'react-icons/fa';
import { useCheckoutFormValuesStore } from '../../stores';
import { useState, useEffect } from 'react';
import { yup, yupResolver } from 'src/utils/form';
import { PagarmeFirstStepSchema } from 'src/components/Pagarme/PagarmeStep01';
import { useForm } from 'react-hook-form';

type Props = {
  field: CartItemType;
  amountOfItemsInCart: number;
  index: number;
  buyerInfo: any;
};

export type SecondaryInfo = {
  ticketTypeId?: string;
  name?: string;
  buyerInfoData?: {
    birth_date?: string;
    city?: string;
    state?: string;
  };
};

const validationSchema = yup.object({
  fullName: yup
    .string()
    .required('Digite o nome e sobrenome')
    .matches(/^[A-Za-zÀ-ÿ]{2,} [A-Za-zÀ-ÿ]+$/, {
      message: 'Digite o nome e sobrenome.',
    }),
  bornAt: yup
    .string()
    .required('Digite a data de nascimento')
    .matches(/[0-9]{2}\/[0-9]{2}\/[0-9]{4}/, {
      message: 'Digite seu nascimento: dd/mm/aaaa',
    })
    .test('', 'Digite uma data de nascimento válida', date => {
      if (!date) return false;

      const [day, month] = date.split('/');

      if (parseInt(day) > 31 || parseInt(day) <= 0) return false;

      if (parseInt(month) > 12 || parseInt(month) <= 0) return false;

      return false;
    }),
  city: yup.string().required('Digite a cidade').min(3, 'No mínimo 3 letras'),
  state: yup
    .string()
    .required('Selecione o estado')
    .min(2, 'Selecione o estado'),
});

const listOfStates = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
  'DF',
];

export const OwnersSectionInput = ({
  field,
  index,
  amountOfItemsInCart,
  buyerInfo,
}: Props) => {
  const {
    invalidFieldsNames,
    invalidFieldsMessage,
    setInvalidFieldsMessage,
    setInvalidFieldsNames,
  } = useEventContext();
  const { formValues, setFormValueOwner } = useCheckoutFormValuesStore();
  const currentFieldKey = `${field.ticketTypeId}-${index}`;
  const currentOwner = formValues.owners?.[currentFieldKey] || {};

  const revertFormatDate = (value: string) => {
    if (value) {
      const [year, month, day] = value.split('-');
      const birthDateObj: string = `${day}/${month}/${year}`;
      return birthDateObj;
    }
  };

  const [name, setName] = useState(currentOwner.name || '');
  const [birthDate, setBirthDate] = useState(
    revertFormatDate(currentOwner.buyerInfoData?.birth_date) || '',
  );
  const [city, setCity] = useState(currentOwner.buyerInfoData?.city || '');
  const [state, setState] = useState(currentOwner.buyerInfoData?.state || '');

  const {
    register,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
  } = useForm<PagarmeFirstStepSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fullName: name,
      bornAt: birthDate,
      city: city,
      state: state,
    },
    reValidateMode: 'onChange', // Altera para revalidar quando o campo perde o foco
  });

  useEffect(() => {
    setInvalidFieldsNames?.([]);
    setInvalidFieldsMessage?.([]);
    clearErrors?.([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateInvalidFields();
    updateBuyerInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, birthDate, city, state, invalidFieldsNames]);

  const validateInvalidFields = () => {
    if (invalidFieldsNames.length === 0) return;
    invalidFieldsNames.forEach((field: any) => trigger(field));
  };

  const updateBuyerInfo = () => {
    const newInfo = createNewInfo();

    setFormValueOwner(currentFieldKey, newInfo);
  };

  const createNewInfo = () => {
    const birthDateObj = formatBirthDate(birthDate);
    return {
      ticketTypeId: `${field.ticketTypeId}`,
      name,
      buyerInfoData: buyerInfo
        ? { birth_date: birthDateObj, city, state }
        : undefined,
    };
  };

  const formatBirthDate = (birthDate: string) => {
    if (!birthDate) return undefined;
    const [day, month, year] = birthDate.split('/');
    return `${year}-${month}-${day}`;
  };

  const getErrorKey = (key: string, value: string) => {
    const error = invalidFieldsMessage.find(item => item.startsWith(key));
    if (error?.includes(value)) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Box>
        <Text wordBreak="break-word" fontSize="lg" fontWeight="500" my={4}>
          {getTitle(amountOfItemsInCart, field.name, index)}
        </Text>

        <FloatingLabelContainer
          borderColor={
            errors.fullName && getErrorKey(currentFieldKey, 'Name')
              ? 'red.500'
              : 'gray.500'
          }
          border={
            errors.fullName && getErrorKey(currentFieldKey, 'Name')
              ? '2px'
              : '1px'
          } // Borda vermelha em caso de erro
        >
          <FloatingLabelInput
            value={name}
            {...register('fullName')}
            onChange={e => {
              setName(e.target.value);
              setValue('fullName', e.target.value);
            }}
            onBlur={() => trigger('fullName')}
          />
          <FloatingLabel>Nome e sobrenome</FloatingLabel>
        </FloatingLabelContainer>

        {errors.fullName && getErrorKey(currentFieldKey, 'Name') && (
          <ErrorMessage>{errors?.fullName?.message}</ErrorMessage>
        )}
      </Box>

      {buyerInfo && (
        <Flex direction="column" gap={5} mt={5}>
          <>
            <FloatingLabelContainerWithAddon
              borderColor={
                errors.bornAt && getErrorKey(currentFieldKey, 'date')
                  ? 'red.500'
                  : 'gray.500'
              }
              border={
                errors.bornAt && getErrorKey(currentFieldKey, 'date')
                  ? '2px'
                  : '1px'
              }
              addonPositon="right"
              addon={
                <Icon as={FaRegCalendar} color="gray.700" boxSize="5" mr="3" />
              }>
              <FloatingLabelInputNumberFormat
                value={birthDate}
                autoComplete="bday"
                type="text"
                {...register('bornAt')}
                format="##/##/####"
                onChange={e => {
                  setBirthDate(e.target.value);
                  setValue('bornAt', e.target.value);
                }}
                onBlur={() => trigger('bornAt')} // Valida o campo quando perde o foco
              />
              <FloatingLabel fontSize="sm">Nascimento</FloatingLabel>
            </FloatingLabelContainerWithAddon>
            {errors.bornAt && getErrorKey(currentFieldKey, 'date') && (
              <ErrorMessage mt={-3}>{errors.bornAt.message}</ErrorMessage>
            )}
          </>
          <Flex gap={5}>
            <Flex direction="column" flex={1}>
              <FloatingLabelContainer
                borderColor={
                  errors.city && getErrorKey(currentFieldKey, 'city')
                    ? 'red.500'
                    : 'gray.500'
                }
                border={
                  errors.city && getErrorKey(currentFieldKey, 'city')
                    ? '2px'
                    : '1px'
                }>
                <FloatingLabelInput
                  {...register('city')}
                  value={city}
                  onChange={e => {
                    setCity(e.target.value);
                    setValue('city', e.target.value);
                  }}
                  onBlur={() => trigger('city')}
                />
                <FloatingLabel>Cidade</FloatingLabel>
              </FloatingLabelContainer>
              {errors.city && getErrorKey(currentFieldKey, 'city') && (
                <ErrorMessage>{errors.city.message}</ErrorMessage>
              )}
            </Flex>

            <Flex direction="column">
              <FloatingLabelContainer
                width="100%"
                borderColor={
                  errors.state && getErrorKey(currentFieldKey, 'state')
                    ? 'red.500'
                    : 'gray.500'
                }
                border={
                  errors.state && getErrorKey(currentFieldKey, 'state')
                    ? '2px'
                    : '1px'
                }>
                <Select
                  paddingEnd={4}
                  border="none"
                  {...register('state')}
                  value={state}
                  placeholder="Estado"
                  onBlur={() => trigger('state')}
                  onChange={e => {
                    setState(e.target.value);
                    setValue('state', e.target.value);
                  }}>
                  {renderStateOptions()}
                </Select>
                <FloatingLabel></FloatingLabel>
              </FloatingLabelContainer>
              {errors.state && getErrorKey(currentFieldKey, 'state') && (
                <ErrorMessage>{errors.state.message}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
      )}
    </>
  );
};

// Função para gerar o título da seção
const getTitle = (
  amountOfItemsInCart: number,
  ticketName: string,
  index: number,
) => {
  return amountOfItemsInCart === 1
    ? `Ingresso: ${ticketName}`
    : `${index + 1}º Ingresso: ${ticketName}`;
};

// Função para gerar as opções de estados
const renderStateOptions = () =>
  listOfStates.map(state => (
    <option value={state} key={state}>
      {state}
    </option>
  ));
